import {
  Box,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { capitalize, isEmpty, isNil, map, toLower } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import visuallyHidden from '@mui/utils/visuallyHidden';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { utcToZonedTime } from 'date-fns-tz';

import { getOnboardedPatientsByPage, sortOnboardedPatients } from 'store/thunks/workQueueThunks';
import { PatientDemographics } from 'enums/patient.enum';
import gender from 'common/constants/gender';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import { SortOrder } from 'enums/common.enum';

const headCells = [
  {
    id: PatientDemographics.FIRSTNAME,
    label: 'Patient Name',
  },
  {
    id: PatientDemographics.GENDER,
    label: 'Gender',
  },
  {
    id: PatientDemographics.DOB,
    label: 'Date of Birth',
  },
  {
    id: PatientDemographics.ORIGINAL_PARTNER,
    label: 'Original Partner',
  },
  {
    id: PatientDemographics.ONBOARDED_DATE,
    label: 'OnBoarded Date',
  },
  {
    id: PatientDemographics.CREATED_TIMESTAMP,
    label: 'Created Date',
  },
];

const PatientQualificationTable = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { sortBy, pages, currentPage } = useSelector(
    ({ workQueue }) => workQueue.patientQualification
  );

  const totalPages = useSelector(({ workQueue }) =>
    Math.ceil(workQueue.patientQualification.count / 25)
  );
  const patients = pages[currentPage];

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      sortOnboardedPatients({
        sortBy: {
          sortKey,
          sortOrder:
            sortBy.sortKey === sortKey && sortBy.sortOrder === SortOrder.ASCENDING
              ? SortOrder.DESCENDING
              : SortOrder.ASCENDING,
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      // page didnt change, don't do anything.
      return;
    }

    dispatch(getOnboardedPatientsByPage({ page }));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Grid container direction='column'>
      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={
                      sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : false
                    }
                  >
                    <TableSortLabel
                      active={sortBy.sortKey === headCell.id}
                      direction={sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'}
                      onClick={handleSort}
                      data-head-property={headCell.id}
                    >
                      {headCell.label}
                      {sortBy.sortKey === headCell.id ? (
                        <Box component='span' sx={visuallyHidden}>
                          {toLower(sortBy.sortOrder) === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {map(patients, (patient) => (
                <TableRow key={patient.mpi} hover>
                  <TableCell component='th' scope='row' align='left'>
                    <Link
                      to={`/patients/${patient.mpi}`}
                      style={{ color: theme.palette.text.primary }}
                    >
                      {patient.patientName}
                    </Link>
                  </TableCell>
                  <TableCell>{gender[patient.gender]}</TableCell>
                  <TableCell>
                    {format(utcToZonedTime(new Date(patient.dob), 'utc'), 'MM/dd/yyyy')}
                  </TableCell>
                  <TableCell>{capitalize(patient.originalPartner.partnerName)}</TableCell>
                  <TableCell>
                    {format(utcToZonedTime(new Date(patient.onboardedAt), 'utc'), 'MM/dd/yyyy')}
                  </TableCell>
                  <TableCell>{format(new Date(patient.createdTime), 'MM/dd/yyyy')}</TableCell>
                </TableRow>
              ))}
            </TableBody>

            {!isNil(patients) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={6} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(patients) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(patients) && isEmpty(patients) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default PatientQualificationTable;
